/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/
import 'bootstrap/dist/css/bootstrap.min.css';
import { useState, useEffect, useMemo } from "react";

// react-router components
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";

// Material Dashboard 2 PRO React examples
import Sidenav from "examples/Sidenav";
import Configurator from "examples/Configurator";

// Material Dashboard 2 PRO React themes
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";

// Material Dashboard 2 PRO React Dark Mode themes
import themeDark from "assets/theme-dark";
import themeDarkRTL from "assets/theme-dark/theme-rtl";

// RTL plugins
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";

// Material Dashboard 2 PRO React routes
import myRoutes from "myRoutes";
import ProductRoute from "ProductRoute";
import Unauthorized from "layouts/pages/unauthorized";

// Material Dashboard 2 PRO React contexts
import { useMaterialUIController, setMiniSidenav, setOpenConfigurator } from "context";

// Images
import brandWhite from "assets/images/logos/Cosmaupload.png";
import brandDark from "assets/images/logos/Cosmaupload.png";

// redux
import { useSelector, useDispatch } from "react-redux";
import * as actions from "redux/actions/auth";
import * as actionTypes from "redux/constants";

// route.key values from myRoutes.js — matched by key (not the numeric id) so
// reordering/adding routes there can't silently break login/register gating.
// "LOGOUT_KEY" looks odd: that nav entry's key is "logout" but it renders the
// public <Login /> page at /auth/login — it's reused for both purposes.
const LOGOUT_KEY = "logout";
const REGISTER_KEYS = new Set([
  "register",
  "register-organisation",
  "register-single-user",
  "register-community",
]);

// Saves the current URL before redirecting unauthenticated users to login,
// so we can restore it after they authenticate.
function SaveAndRedirect({ to }) {
  const { pathname, search } = useLocation();
  useEffect(() => {
    const isAuthPath = pathname === "/auth/login" || pathname.startsWith("/auth/") || pathname === "/";
    if (!isAuthPath) {
      sessionStorage.setItem("cosma_intended", pathname + search);
    }
  }, []);
  return <Navigate to={to} />;
}

export default function App() {
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    direction,
    layout,
    openConfigurator,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const myDispatch = useDispatch();

  const auth = useSelector((state) => state.auth);

  const isAuth = auth.isAuth;

  useEffect(() => {
    if (isAuth && (pathname === "/" || pathname === "/auth/login")) {
      // Restore the page the user was trying to reach before being sent to login
      const intended = sessionStorage.getItem("cosma_intended");
      if (intended) {
        sessionStorage.removeItem("cosma_intended");
        navigate(intended);
      } else {
        navigate(`/pages/dashboard`);
      }
    }
  }, [isAuth]);

  const hiddenKeys = actionTypes.HIDE_ROUTES(auth.user?.role);

  const routes = myRoutes.reduce((res, ele1) => {
    const collapse = ele1.collapse.reduce((res2, ele2) => {
      if (!hiddenKeys.includes(ele2.key)) res2.push(ele2);
      return res2;
    }, []);
    if (!hiddenKeys.includes(ele1.key)) {
      res.push({
        ...ele1,
        collapse,
      });
    }
    return res;
  }, []);

  const sidebarRoutes = myRoutes.reduce((res, ele1) => {
    const collapse = ele1.collapse.reduce((res2, ele2) => {
      if (!hiddenKeys.includes(ele2.key) && !ele2.hidden) res2.push(ele2);
      return res2;
    }, []);
    if (!hiddenKeys.includes(ele1.key) && !ele1.hidden) {
      res.push({
        ...ele1,
        collapse,
      });
    }
    return res;
  }, []);

  // Cache for the rtl
  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });

    setRtlCache(cacheRtl);
  }, []);

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // Change the openConfigurator state
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const handleClick = (e, clickedName) => {
    if (clickedName === "Logout") myDispatch(actions.logout());
  };

  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (isAuth && route.route && route.key !== LOGOUT_KEY && !REGISTER_KEYS.has(route.key)) {
        const element = route.products
          ? <ProductRoute allowedProducts={route.products}>{route.component}</ProductRoute>
          : route.component;
        return <Route exact path={route.route} element={element} key={route.key} />;
      } else if (!isAuth && (route.key === LOGOUT_KEY || REGISTER_KEYS.has(route.key))) {
        return <Route exact path={route.route} element={route.component} key={route.key} />;
      }

      return null;
    });

  const configsButton = (
    <MDBox
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="3.25rem"
      height="3.25rem"
      bgColor="white"
      shadow="sm"
      borderRadius="50%"
      position="fixed"
      right="2rem"
      bottom="2rem"
      zIndex={99}
      color="dark"
      sx={{ cursor: "pointer" }}
      onClick={handleConfiguratorOpen}
    >
      <Icon fontSize="small" color="inherit">
        settings
      </Icon>
    </MDBox>
  );

  return direction === "rtl" ? (
    <CacheProvider value={rtlCache}>
      <ThemeProvider theme={darkMode ? themeDarkRTL : themeRTL}>
        <CssBaseline />
        {layout === "dashboard" && (
          <>
            <Sidenav
              color={sidenavColor}
              brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
              brandName="COSMA APP PRO"
              routes={sidebarRoutes}
              onMouseEnter={handleOnMouseEnter}
              onMouseLeave={handleOnMouseLeave}
              handleClick={handleClick}
              handleConfiguratorOpen={handleConfiguratorOpen}
            />
            <Configurator />
            {configsButton}
          </>
        )}
        {layout === "vr" && <Configurator />}
        <Routes>
          {getRoutes(routes)}
          <Route path="/unauthorized" element={<Unauthorized />} />
          {isAuth ? (
            <Route path="*" element={<Navigate to="/pages/dashboard" />} />
          ) : (
            <Route path="*" element={<SaveAndRedirect to="/auth/login" />} />
          )}
        </Routes>
      </ThemeProvider>
    </CacheProvider>
  ) : (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <CssBaseline />
      {layout === "dashboard" && (
        <>
          <Sidenav
            color={sidenavColor}
            brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
            brandName="COSMA APP PRO"
            routes={sidebarRoutes}
            onMouseEnter={handleOnMouseEnter}
            onMouseLeave={handleOnMouseLeave}
            handleClick={handleClick}
            handleSettings={handleConfiguratorOpen}
          />
          <Configurator />
          {configsButton}
        </>
      )}
      {layout === "vr" && <Configurator />}
      <Routes>
        {getRoutes(routes)}
        {isAuth ? (
          <Route path="*" element={<Navigate to="/pages/dashboard" />} />
        ) : (
          <Route path="*" element={<SaveAndRedirect to="/auth/login" />} />
        )}
      </Routes>
    </ThemeProvider>
  );
}
